<template>
  <section v-if="!isReady">
    <p-loading />
  </section>
  <section v-else class="uspto">
    <header>
      <h1>USPTO dashboard</h1>
    </header>
    <section v-if="$hasPermission('uspto')" class="action-wrapper">
      <template v-if="onlyUnmatched">
        <p-button color="danger" class="remove-deleted-button" @click="removeDeleted">Remove unmatched ({{ toDelete.length }})</p-button>
      </template>
      <p-button color="primary" :disabled="syncInProgress" @click="sync">Sync with USPTO</p-button>
      <p-button v-if="active" color="secondary" @click="pause">Pause downloading</p-button>
      <p-button v-else color="primary" @click="resume">Resume downloading</p-button>
    </section>
    <section class="uspto-list-wrapper">
      <p-uspto-inspect-modal
        v-if="inspectModalVisible"
        :message="itemToInspect && itemToInspect.message"
        :stack="itemToInspect && itemToInspect.stack"
        @close="inspectModalVisible = false"
      />
      <div class="header-wrapper">
        <div class="filter">
          <p-checkbox v-model="filter.queued" label="Queued" @change="onFilterChange"></p-checkbox>
          <p-checkbox v-model="filter.requeued" label="Requeued" @change="onFilterChange"></p-checkbox>
          <p-checkbox v-model="filter.inprogress" label="In Progress" @change="onFilterChange"></p-checkbox>
          <p-checkbox v-model="filter.completed" label="Completed" @change="onFilterChange"></p-checkbox>
          <p-checkbox v-model="filter.failed" label="Failed" @change="onFilterChange"></p-checkbox>
          <p-checkbox v-model="filter.dead" label="Dead" @change="onFilterChange"></p-checkbox>
          <p-checkbox v-model="filter.delete" label="Delete" @change="onFilterChange"></p-checkbox>
          <p-checkbox v-model="filter.warning" label="Warning" @change="onFilterChange"></p-checkbox>
          <p-checkbox v-model="filter.isUnmatched" label="Is unmatched" @change="onFilterChange"></p-checkbox>
        </div>
        <div>Sort by:<p-dropdown :value="sort" :options="options.sorting" @change="setSort"></p-dropdown></div>
        <div><label>Total:</label>&nbsp;{{ total }}</div>
      </div>
      <ul class="uspto-list">
        <li class="uspto-list-item header">
          <div>Id</div>
          <div>Changed At</div>
          <div>Customer Number</div>
          <div>Status</div>
          <div>UnMatched</div>
          <div>Scheduled At</div>
          <div>Finished At</div>
          <div>Message</div>
          <div>Priority</div>
          <div></div>
        </li>
      </ul>
      <ul class="uspto-list">
        <li v-for="entry in collection" :key="entry.id" class="uspto-list-item">
          <div>
            <span>{{ entry.id }}</span>
          </div>
          <div>
            {{ entry.applicationChangedAt && new Date(entry.applicationChangedAt).toLocaleString() }}
          </div>
          <div>
            {{ entry.customerNumber }}
          </div>
          <div :style="{ color: STATUS[entry.status].color }">
            {{ STATUS[entry.status].label }}
          </div>
          <div>
            {{ entry.isUnmatched ? 'Yes' : '' }}
          </div>
          <div>{{ entry.createdAt && new Date(entry.createdAt).toLocaleString() }}</div>
          <div v-if="entry.status === 'completed' || entry.status === 'failed' || entry.status === 'dead'">
            {{ entry.updatedAt && new Date(entry.updatedAt).toLocaleString() }}
          </div>
          <div v-else></div>
          <div>{{ entry.message }}</div>
          <div class="priority-cell">
            <template v-if="editingPriorityId === entry.id">
              <input v-model.number="priority" type="number" style="width: 40px;" />
              <p-button variant="text" color="primary" title="Save priority" @click="changePriority">&#10003;</p-button>
              <p-button variant="text" color="secondary" title="Cancel editing" @click="editingPriorityId = null">&times;</p-button>
            </template>
            <template v-else>
              <span>
                {{ entry.priority }}
              </span>
              <p-button
                v-if="$hasPermission('uspto') && (entry.status === 'queued' || entry.status === 'requeued')"
                class="edit-priority-button"
                variant="text"
                color="secondary"
                title="Edit priority"
                @click="editPriority(entry)"
                >&#9998;</p-button
              >
            </template>
          </div>
          <div class="uspto-list-item-actions">
            <p-button
              v-if="$hasPermission('uspto') && entry.message"
              variant="text"
              color="secondary"
              title="Inspect this application"
              @click="inspect(entry)"
              >≡</p-button
            >
            <p-button
              v-if="$hasPermission('uspto') && entry.status !== 'queued' && entry.status !== 'requeued'"
              variant="text"
              title="Requeue this application"
              @click="requeue(entry.id)"
              >&#8634;</p-button
            >
            <p-button
              v-if="$hasPermission('uspto') && entry.status !== 'inprogress'"
              variant="text"
              title="Delete this application"
              @click="remove(entry.id)"
              >&times;</p-button
            >
          </div>
        </li>
      </ul>
      <Pagination :total="total" :size="20" :from="from" @navigate="onNavigate" />
    </section>
    <section class="schedule-wrapper">
      <div class="schedule-control-form">
        <p-button color="primary" @click="syncCustomerNumbers">Sync customer numbers</p-button>

        <p-cron-config v-model="scheduleValueLocal" class="sync-customers-no-button" label="USPTO Schedule Cron" />
        <div v-if="scheduleDefault" class="default-value">Default: {{ scheduleDefault }}</div>
        <p-button color="primary" @click="updateSchedule">Update USPTO cron config</p-button>

        <p-duration-config v-model="downloadPauseTime" label="Too Many Requests Delay" />
        <div v-if="tooManyRequestsDelayDefault" class="default-value">Default: {{ formatDuration(tooManyRequestsDelayDefault) }}</div>
        <p-button color="primary" @click="updateTooManyRequestsDelay">Update Delay</p-button>

        <p-duration-config v-model="requestTimeoutValueLocal" label="Request Timeout" />
        <div v-if="requestTimeoutDefault" class="default-value">Default: {{ formatDuration(requestTimeoutDefault) }}</div>
        <p-button color="primary" @click="updateRequestTimeout">Update Timeout</p-button>

        <p-duration-config v-model="downloadRepeatValueLocal" label="Download Repeat Interval" />
        <div v-if="downloadRepeatDefault" class="default-value">Default: {{ formatDuration(downloadRepeatDefault) }}</div>
        <p-button color="primary" @click="updateDownloadRepeat">Update Interval</p-button>

        <div class="schedule-input-wrapper">
          <header>
            <h2>Schedule</h2>
          </header>
          <p-uspto-schedule />
        </div>
      </div>
    </section>
    <section class="chart-wrapper">
      <div class="tabs">
        <p-button variant="text" class="tab" :class="{ active: summaryTabActive }" @click="currentTab = 'summary'">Summary</p-button>
        <p-button variant="text" class="tab" :class="{ active: customerTabActive }" @click="currentTab = 'customer'">Customer Summary</p-button>
      </div>
      <div class="tab-content">
        <p-pie-chart v-if="summaryTabActive && !!aggs && Object.keys(aggs).length" :key="aggsHash" class="pie-chart" :aggs="aggs" />
        <p-pie-chart-customers-info
          v-if="customerTabActive && !!aggsCustomers && Object.keys(aggsCustomers).length"
          :key="aggsCustomersHash"
          class="pie-chart-customers"
          :aggs="aggsCustomers"
        />
      </div>
    </section>
  </section>
</template>

<script>
import { mapState } from 'vuex';

import { lw } from '@/utils/lw';

import Loading from '@/components/common/Loading';
import Checkbox from '@/components/common/Checkbox';
import Button from '@/components/common/Button';
import Dropdown from '@/components/common/Dropdown';

import PieChart from './PieChart.js';
import PieChartCustomersInfo from './PieChartCustomersInfo.js';
import Schedule from './Schedule';

import InspectModal from './InspectModal';

import { STATUS } from './constants';

import Pagination from '@/components/common/Pagination.vue';
import CronConfig from './components/CronConfig.vue';
import DurationConfig from './components/DurationConfig.vue';
const LOCAL_STORAGE_USPTO_STATUS_LIST = 'LOCAL_STORAGE_USPTO_STATUS_LIST';
const LOCAL_STORAGE_USPTO_SORT_KEY = 'LOCAL_STORAGE_USPTO_SORT_KEY';

const STATUS_LIST = Object.keys(STATUS);

export default {
  components: {
    'p-loading': Loading,
    'p-button': Button,
    'p-dropdown': Dropdown,
    'p-checkbox': Checkbox,
    'p-pie-chart': PieChart,
    'p-pie-chart-customers-info': PieChartCustomersInfo,
    'p-uspto-schedule': Schedule,
    'p-uspto-inspect-modal': InspectModal,
    'p-cron-config': CronConfig,
    'p-duration-config': DurationConfig,
    Pagination
  },
  data() {
    const statuses = lw.get(LOCAL_STORAGE_USPTO_STATUS_LIST) || ['inprogress', 'failed'];

    return {
      sort: lw.get(LOCAL_STORAGE_USPTO_SORT_KEY) || '',
      filter: STATUS_LIST.reduce(
        (obj, key) => ({
          ...obj,
          [key]: statuses.includes(key)
        }),
        {
          isUnmatched: statuses.includes('isUnmatched')
        }
      ),
      downloadPauseTime: null,
      scheduleValueLocal: '',
      requestTimeoutValueLocal: null,
      downloadRepeatValueLocal: null,
      options: {
        sorting: [
          { id: 'status', label: 'Status' },
          { id: 'newest', label: 'Newest' },
          { id: 'oldest', label: 'Oldest' },
          { id: 'priority', label: 'Priority' }
        ]
      },
      autorefreshInterval: null,
      inspectModalVisible: false,
      itemToInspect: null,
      from: 0,
      currentTab: 'summary',
      STATUS,
      editingPriorityId: null,
      priority: null
    };
  },
  computed: {
    ...mapState({
      total: s => s.uspto.total,
      collection: s => s.uspto.collection,
      aggs: s => s.uspto.aggs,
      aggsCustomers: s => s.uspto.aggsCustomers,
      // prettier-ignore
      aggsHash: s => `${s.uspto.aggs.queued || '0'}${s.uspto.aggs.requeued || '0'}${s.uspto.aggs.inprogress || '0'}${s.uspto.aggs.completed || '0'}${s.uspto.aggs.failed || '0'}${s.uspto.aggs.dead || '0'}`,
      aggsCustomersHash: s => {
        const customers = s.uspto?.aggsCustomers || {};
        return Object.values(customers).join('');
      },
      isReady: s => s.uspto.isReady,
      active: s => s.uspto.active,
      syncInProgress: s => s.uspto.syncInProgress,
      toDelete: s => s.uspto.toDelete,
      cronJobTimer: s => s.uspto.cronJobTimer,
      schedule: s => s.uspto.schedule,
      requestTimeout: s => s.uspto.requestTimeout,
      downloadRepeat: s => s.uspto.downloadRepeat,
      tooManyRequestsDelay: s => s.uspto.tooManyRequestsDelay
    }),
    scheduleValue() {
      return this.schedule?.current || '';
    },
    scheduleDefault() {
      return this.schedule?.default || '';
    },
    requestTimeoutValue() {
      return this.requestTimeout?.current || null;
    },
    requestTimeoutDefault() {
      return this.requestTimeout?.default || null;
    },
    downloadRepeatValue() {
      return this.downloadRepeat?.current || null;
    },
    downloadRepeatDefault() {
      return this.downloadRepeat?.default || null;
    },
    tooManyRequestsDelayValue() {
      return this.tooManyRequestsDelay?.current || null;
    },
    tooManyRequestsDelayDefault() {
      return this.tooManyRequestsDelay?.default || null;
    },
    onlyUnmatched() {
      return this.filter.isUnmatched && Object.values(this.filter).filter(v => v).length === 1;
    },
    summaryTabActive() {
      return this.currentTab === 'summary';
    },
    customerTabActive() {
      return this.currentTab === 'customer';
    }
  },
  watch: {
    filter: {
      handler() {
        lw.set(
          LOCAL_STORAGE_USPTO_STATUS_LIST,
          Object.keys(this.filter).filter(key => !!this.filter[key])
        );
      },
      deep: true
    },
    scheduleValue(newVal) {
      this.scheduleValueLocal = newVal || '';
    },
    requestTimeoutValue(newVal) {
      this.requestTimeoutValueLocal = newVal;
    },
    downloadRepeatValue(newVal) {
      this.downloadRepeatValueLocal = newVal;
    },
    tooManyRequestsDelayValue(newVal) {
      this.downloadPauseTime = newVal;
    }
  },
  created() {
    this.autorefreshInterval = 30 * 1000;
    this.autorefresh();
    this.loadConfigData();
  },
  destroyed() {
    this.autorefreshInterval = null;
  },
  methods: {
    editPriority(entry) {
      this.editingPriorityId = entry.id;
      this.priority = entry.priority;
    },
    async changePriority() {
      try {
        await this.$store.dispatch('uspto/update', { id: this.editingPriorityId, priority: this.priority });
      } catch (e) {
        this.$toast.error({
          title: 'Update priority failed',
          message: `Please, try again later or contact our development team.`
        });
      } finally {
        this.editingPriorityId = null;
        this.priority = null;
      }
    },
    async removeDeleted() {
      const confirmResult = await this.$confirm({
        title: 'Delete applications?',
        message: `Are you sure you want to delete ${this.toDelete.length} application(s) from queue?\n\nThis action may take some time.`,
        confirm: 'Delete'
      });

      if (!confirmResult) {
        return;
      }

      try {
        const lock = this.$lock();
        const result = await this.$store.dispatch('uspto/syncDelete');
        await this.loadData();
        lock.release();

        let message = '';
        for (const key of Object.getOwnPropertyNames(result)) {
          var value = result[key];
          message += `${key}: ${value}\n`;
        }

        this.$toast.success({ message: message.trim() }, 10000);
      } catch (e) {
        this.$toast.error({ message: `Failed to sync applications. Please try later...` });
      }
    },
    async sync() {
      const confirmResult = await this.$confirm({
        title: 'Sync applications',
        message: `Are you sure you want to sync with Patent Center now?\n\nThis action may take some time.`,
        confirm: 'Sync'
      });

      if (!confirmResult) {
        return;
      }

      try {
        const result = await this.$store.dispatch('uspto/syncMark');
        await this.loadData();

        let message = '';
        for (const key of Object.getOwnPropertyNames(result)) {
          var value = result[key];
          message += `${key}: ${value}\n`;
        }

        this.$toast.success({ message: message.trim() }, 10000);
      } catch (e) {
        this.$toast.error({ message: `Failed to sync applications. Please try later...` });
      }
    },
    async pause() {
      try {
        const lock = this.$lock();
        await this.$store.dispatch('uspto/pause');
        lock.release();

        this.$toast.success({ message: `Processing paused` });
      } catch (e) {
        this.$toast.error({ message: `Failed to pause processing` });
      }
    },
    async resume() {
      try {
        const lock = this.$lock();
        await this.$store.dispatch('uspto/resume');
        lock.release();

        this.$toast.success({ message: `Processing resumed` });
      } catch (e) {
        this.$toast.error({ message: `Failed to resume processing` });
      }
    },
    async requeue(id) {
      try {
        const lock = this.$lock();
        await this.$store.dispatch('uspto/create', { applications: [id] });
        lock.release();

        this.$toast.success({
          title: 'Requeue completed',
          message: `Application '${id}' was scheduled for processing.`
        });
      } catch (e) {
        this.$toast.error({
          title: 'Requeue failed',
          message: `Please, try again later or contact our development team.`
        });
      }
    },
    async remove(id) {
      try {
        const lock = this.$lock();
        await this.$store.dispatch('uspto/delete', id);
        lock.release();

        this.$toast.success({
          title: 'Delete completed',
          message: `Application '${id}' was deleted.`
        });
      } catch (e) {
        this.$toast.error({
          title: 'Delete failed',
          message: `Please, try again later or contact our development team.`
        });
      }
    },
    setSort(id) {
      const option = this.options.sorting.find(option => option.id === id);
      if (!option) {
        return;
      }
      lw.set(LOCAL_STORAGE_USPTO_SORT_KEY, id);

      this.sort = id;
      this.skip = 0;

      this.loadData();
    },
    async autorefresh() {
      while (this.autorefreshInterval) {
        try {
          await this.loadData();
        } catch (e) {
          this.autorefreshInterval = null;
        }
        await new Promise(resolve => setTimeout(resolve, this.autorefreshInterval));
      }
    },
    inspect(item) {
      this.inspectModalVisible = true;
      this.itemToInspect = item;
    },
    loadData() {
      this.$store.dispatch('uspto/getCollection', {
        skip: this.from,
        limit: 20,
        status: Object.keys(this.filter)
          .filter(key => key !== 'isUnmatched')
          .filter(key => !!this.filter[key]),
        isUnmatched: this.filter.isUnmatched,
        sort: this.sort
      });
      this.$store.dispatch('uspto/initialize');
    },
    onNavigate({ from }) {
      this.from = from;
      this.loadData();
    },
    onFilterChange() {
      this.from = 0;
      this.loadData();
    },
    async syncCustomerNumbers() {
      const confirmResult = await this.$confirm({
        title: 'Sync customer numbers',
        message: `Are you sure you want to sync customer numbers from Harrity database to applications state?\n\nThis action may take some time.`,
        confirm: 'Sync'
      });

      if (!confirmResult) {
        return;
      }

      try {
        const lock = this.$lock();
        const result = await this.$store.dispatch('uspto/syncCustomerNumbers');
        await this.loadData();
        lock.release();

        let message = `Customer numbers sync completed:\n`;
        message += `Updated: ${result.updated || 0}\n`;
        message += `Skipped: ${result.skipped || 0}\n`;
        if (result.errors && result.errors.length > 0) {
          message += `Errors: ${result.errors.length}\n`;
          result.errors.slice(0, 3).forEach((error, index) => {
            message += `${index + 1}. ${error}\n`;
          });
          if (result.errors.length > 3) {
            message += `... and ${result.errors.length - 3} more errors`;
          }
        }

        this.$toast.success({ message: message.trim() }, 10000);
      } catch (e) {
        this.$toast.error({ message: `Failed to sync customer numbers. Please try later...` });
      }
    },
    async updateSchedule() {
      if (!this.scheduleValueLocal.trim()) {
        this.$toast.error({ message: 'Please enter a valid cron expression' });
        return;
      }
      try {
        const lock = this.$lock();
        await this.$store.dispatch('uspto/updateSchedule', this.scheduleValueLocal);
        lock.release();
        this.$toast.success({ message: 'Schedule updated successfully' });
      } catch (e) {
        this.$toast.error({ message: 'Failed to update schedule' });
      }
    },
    async updateRequestTimeout() {
      if (!this.requestTimeoutValueLocal) {
        this.$toast.error({ message: 'Please enter a valid timeout value' });
        return;
      }
      try {
        const lock = this.$lock();
        await this.$store.dispatch('uspto/updateRequestTimeout', this.requestTimeoutValueLocal);
        lock.release();
        this.$toast.success({ message: 'Request timeout updated successfully' });
      } catch (e) {
        this.$toast.error({ message: 'Failed to update request timeout' });
      }
    },
    async updateDownloadRepeat() {
      if (!this.downloadRepeatValueLocal) {
        this.$toast.error({ message: 'Please enter a valid interval value' });
        return;
      }
      try {
        const lock = this.$lock();
        await this.$store.dispatch('uspto/updateDownloadRepeat', +this.downloadRepeatValueLocal);
        lock.release();
        this.$toast.success({ message: 'Download repeat interval updated successfully' });
      } catch (e) {
        this.$toast.error({ message: 'Failed to update download repeat interval' });
      }
    },
    async updateTooManyRequestsDelay() {
      if (!this.downloadPauseTime) {
        this.$toast.error({ message: 'Please enter a valid delay value' });
        return;
      }
      try {
        const lock = this.$lock();
        await this.$store.dispatch('uspto/updateTooManyRequestsDelay', +this.downloadPauseTime);
        lock.release();
        this.$toast.success({ message: 'Too many requests delay updated successfully' });
      } catch (e) {
        this.$toast.error({ message: 'Failed to update delay' });
      }
    },
    formatDuration(ms) {
      if (!ms || ms === 0) return 'No delay';

      const days = Math.floor(ms / (24 * 60 * 60 * 1000));
      const hours = Math.floor((ms % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
      const minutes = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
      const seconds = Math.floor((ms % (60 * 1000)) / 1000);

      const parts = [];
      if (days > 0) parts.push(`${days}d`);
      if (hours > 0) parts.push(`${hours}h`);
      if (minutes > 0) parts.push(`${minutes}m`);
      if (seconds > 0) parts.push(`${seconds}s`);

      return parts.join(' ') || 'No delay';
    },
    async loadConfigData() {
      try {
        await Promise.all([
          this.$store.dispatch('uspto/getSchedule'),
          this.$store.dispatch('uspto/getRequestTimeout'),
          this.$store.dispatch('uspto/getDownloadRepeat'),
          this.$store.dispatch('uspto/getTooManyRequestsDelay')
        ]);
      } catch {
        this.$toast.error('Failed to load configuration data:', e);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.uspto {
  display: grid;
  padding: 0 0.5rem 0 1rem;
  grid-template-columns: minmax(0, 3fr) minmax(0, 1.5fr);
  grid-template-rows: max-content minmax(0, 1fr) minmax(0, 1fr);
  grid-gap: 0.5rem;
  width: 100%;
  height: 100%;

  > header {
    grid-column: 1/2;
    grid-row: 1/2;
    padding: 0.5rem 0.5rem 0;
    display: flex;

    box-sizing: border-box;

    h1 {
      color: var(--theme-on-surface);
      font-size: 1.5rem;
      font-weight: 700;
      margin-right: 0.25rem;
    }

    .status-label {
      display: inline-flex;
      line-height: 1;
      height: 1rem;
      justify-content: center;
      align-items: center;
      border-radius: 10%;
      padding: 0.1rem 0.25rem;
      font-size: 0.75rem;
      font-weight: 700;

      &.healthy {
        color: var(--theme-on-success);
        background-color: var(--theme-success);
      }

      &.unhealthy {
        color: var(--theme-on-error);
        background-color: var(--theme-error);
      }
    }
  }

  .action-wrapper {
    grid-column: 2/3;
    grid-row: 1/2;

    display: flex;
    justify-content: flex-end;
    gap: 10px;
    align-items: center;

    .remove-deleted-button {
      margin-right: 10px;
    }
  }

  .uspto-list-wrapper,
  .schedule-wrapper,
  .chart-wrapper {
    background-color: var(--theme-surface);
    padding: 1rem;
    position: relative;

    h2 {
      color: var(--theme-on-surface);
      font-size: 0.8rem;
      font-weight: 500;
      padding: 0;
      margin: 0;
      text-transform: uppercase;
    }
  }

  .uspto-list-wrapper {
    display: grid;
    grid-template-rows: 20px max-content max-content minmax(0, 1fr) max-content;
    overflow-x: scroll;
    grid-column: 1/2;
    grid-row: 2/4;

    .header-wrapper {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      font-size: 0.75rem;
      margin: 0;
      padding: 0;
      font-weight: 400;
      margin-right: 6px;
      padding-bottom: 1rem;

      label {
        font-weight: 500;
      }

      > * {
        height: 100%;
        display: flex;
        align-items: center;

        &:not(:last-child) {
          margin-right: 2rem;
        }
      }

      .filter {
        > * {
          &:not(:last-child) {
            margin-right: 0.5rem;
          }
        }
      }
    }
  }

  .uspto-list {
    margin: 0;
    padding: 0;
    list-style: none;

    .uspto-list-item {
      display: grid;
      grid-template-columns: 130px 175px 175px 100px 75px 175px 175px 100px 100px 100px;
      grid-gap: 0;
      border-bottom: 1px solid var(--theme-highlight);
      font-size: 0.75rem;
      height: 1.75rem;
      align-items: center;

      > div {
        border-left: 1px solid var(--theme-highlight);
        height: 100%;
        display: flex;
        align-items: center;
        padding: 0 0.5rem;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        &:last-child {
          border-left: 1px solid transparent;
          border-right: 1px solid var(--theme-highlight);
        }

        > span {
          text-overflow: ellipsis;
          overflow: hidden;
        }
      }

      &:first-child {
        border-top: 1px solid var(--theme-highlight);
      }

      &:hover {
        background-color: var(--theme-highlight);
        .uspto-list-item-actions {
          display: flex;
          justify-content: flex-end;
        }
        .edit-priority-button {
          visibility: visible;
        }
      }

      .edit-priority-button {
        visibility: hidden;
      }

      .priority-cell {
        display: flex;
        justify-content: space-between;
        gap: 5px;
      }

      &.header {
        font-size: 0.75rem;
        color: var(--theme-on-background-accent);

        > * {
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
          padding: 0 0.5rem;
        }
      }

      .uspto-list-item-actions {
        display: none;
      }
    }

    &:not(:last-child) {
      margin-right: 6px;
    }

    &:last-child {
      overflow-y: scroll;
    }
  }

  .chart-wrapper {
    display: grid;
    grid-template-rows: max-content minmax(0, 1fr);
    grid-gap: 0.5rem;
  }

  .schedule-control-form {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    > .schedule-input-wrapper {
      margin-top: 0.5rem;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }
    > .sync-customers-no-button {
      margin-top: 0.5rem;
    }
  }

  .chart-wrapper {
    grid-row: 2 / 3;
    grid-column: 2 / 3;
    display: grid;
    grid-template-rows: max-content 1fr;

    > * {
      min-width: 0;
      overflow: hidden;
    }

    .tabs {
      display: flex;
      border-bottom: 1px solid var(--theme-highlight);
      margin-bottom: 1rem;

      .tab {
        border: none;
        background: none;
        color: var(--theme-on-surface-accent);
        border-bottom: 2px solid transparent;
        padding: 0.5rem 1rem;
        cursor: pointer;

        &.active {
          color: var(--theme-primary);
          border-bottom-color: var(--theme-primary);
        }

        &:hover {
          color: var(--theme-on-surface);
          background-color: var(--theme-highlight);
        }
      }
    }

    .pie-chart,
    .pie-chart-customers {
      height: 100%;
      width: 100%;
      min-width: 0px;
    }

    .tab-content {
      flex: 1;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      min-height: 300px;
    }
  }

  .schedule-wrapper {
    grid-row: 3/4;
    grid-column: 2/3;
    overflow-y: auto;

    .default-value {
      font-size: 0.75rem;
      color: var(--theme-on-surface-accent);
      margin-top: 0.25rem;
      margin-bottom: 0.5rem;
    }
  }
}
</style>
